import {RegionType} from "@pg-rp/common-types";

interface IAddressOffer {
    address: string;
    region: {
        city?: string | null;
        district?: string | null;
        full_name: string;
        short_name: string;
        town?: string | null;
    };
    stats?: {distance_from_region?: {region?: string | null}} | null;
    street_name: string | null;
    street_number: string | null;
}

interface ISearchedRegion {
    full_name?: string;
    fromOfferStats?: boolean;
    type: number;
}

interface ISelectableSearchedRegion {
    full_name: string;
    id: number;
    type: number;
}

export interface IOfferCardAddressContext {
    /**
     * City names are needed only for legacy offer responses which expose a region path but no
     * explicit city/district fields (for example the vendor-detail offer list).
     */
    regionPathCityNames?: readonly string[];
    searchDistance?: number | string | null;
    searchedRegion?: ISearchedRegion;
}

interface IGetOfferCardAddressContextParams {
    searchedRegionQuery?: string | string[] | null;
    searchDistance?: number | string | null;
    selectedRegions: ISelectableSearchedRegion[];
}

export const getOfferCardAddressContext = ({
    searchedRegionQuery,
    searchDistance,
    selectedRegions
}: IGetOfferCardAddressContextParams): IOfferCardAddressContext => {
    const searchedRegionIds = Array.isArray(searchedRegionQuery) ? searchedRegionQuery : searchedRegionQuery ? [searchedRegionQuery] : [];
    const searchedRegion = searchedRegionIds.length === 1 ? selectedRegions.find((region) => region.id === Number(searchedRegionIds[0])) : undefined;

    return {searchedRegion, searchDistance};
};

/**
 * Builds the address shown on an offer tile. The locality is based on the search narrowing, while
 * radius searches additionally distinguish offers that belong to the searched region from offers
 * pulled in from its surroundings.
 */
export const getOfferCardAddress = (offer: IAddressOffer, context: IOfferCardAddressContext = {}): string => {
    const street = [offer.street_name, offer.street_number].filter(Boolean).join(" ");
    const locality = getOfferCardLocality(offer, context);

    return [street, locality].filter(Boolean).join(", ") || offer.address;
};

const getOfferCardLocality = (offer: IAddressOffer, context: IOfferCardAddressContext): string | null | undefined => {
    const {regionPathCityNames, searchedRegion, searchDistance} = context;
    const inferredLocality = getLocalityFromRegionPath(offer.region.full_name, regionPathCityNames);
    const city = offer.region.city || offer.region.town || inferredLocality.city;
    const hasExplicitDistrictData = Object.prototype.hasOwnProperty.call(offer.region, "district");
    const isCitySearch = searchedRegion?.type === RegionType.CITY || searchedRegion?.type === RegionType.TOWN;
    const regionLevelBelowSearch =
        !hasExplicitDistrictData && isCitySearch && searchedRegion.full_name
            ? getRegionLevelBelowSearch(offer.region.full_name, searchedRegion.full_name)
            : undefined;
    const hasRegionLevelBelowSearchData = regionLevelBelowSearch !== undefined;
    let district = inferredLocality.district;

    if (hasExplicitDistrictData) {
        district = offer.region.district;
    } else if (hasRegionLevelBelowSearchData) {
        district = regionLevelBelowSearch;
    }

    const isDistrictKnown = hasExplicitDistrictData || hasRegionLevelBelowSearchData || inferredLocality.hasMatchedCity;
    const isRadiusSearch = Number(searchDistance) > 0;
    const isOutsideSearchedRegion = isRadiusSearch && isOfferOutsideSearchedRegion(offer, searchedRegion, city);

    switch (searchedRegion?.type) {
        case RegionType.CITY:
        case RegionType.TOWN:
            return isOutsideSearchedRegion ? city || offer.region.short_name : isDistrictKnown ? district : offer.region.short_name;
        case RegionType.DISTRICT:
        case RegionType.NEIGHBORHOOD:
            return isOutsideSearchedRegion ? district || city || offer.region.short_name : null;
        default:
            return city || district || offer.region.short_name;
    }
};

interface IInferredLocality {
    city?: string;
    district?: string | null;
    hasMatchedCity: boolean;
}

const getLocalityFromRegionPath = (fullName: string, cityNames?: readonly string[]): IInferredLocality => {
    if (!cityNames?.length) {
        return {hasMatchedCity: false};
    }

    const regionPath = splitRegionPath(fullName);
    const normalizedCityNames = new Set(cityNames.map(normalizeRegionName));
    const cityIndex = regionPath.findIndex((regionName) => normalizedCityNames.has(normalizeRegionName(regionName)));

    if (cityIndex === -1) {
        return {hasMatchedCity: false};
    }

    return {
        city: regionPath[cityIndex],
        district: regionPath[cityIndex + 1] ?? null,
        hasMatchedCity: true
    };
};

/** `undefined` means paths do not match; `null` means the offer is exactly in the searched region. */
const getRegionLevelBelowSearch = (offerRegionFullName: string, searchedRegionFullName: string): string | null | undefined => {
    const offerRegionPath = splitRegionPath(offerRegionFullName);
    const searchedRegionPath = splitRegionPath(searchedRegionFullName);
    const isSearchedRegionPrefix = searchedRegionPath.every(
        (regionName, index) => normalizeRegionName(regionName) === normalizeRegionName(offerRegionPath[index] ?? "")
    );

    if (!isSearchedRegionPrefix) {
        return undefined;
    }

    return offerRegionPath[searchedRegionPath.length] ?? null;
};

/** Membership that cannot be determined counts as "inside", so the address stays as narrow as possible. */
const isOfferOutsideSearchedRegion = (offer: IAddressOffer, searchedRegion: ISearchedRegion | undefined, offerCity: string | null | undefined): boolean => {
    if (!searchedRegion) {
        return false;
    }

    if (searchedRegion.fromOfferStats) {
        const searchedRegionName = offer.stats?.distance_from_region?.region;

        return Boolean(searchedRegionName && offerCity && normalizeRegionName(offerCity) !== normalizeRegionName(searchedRegionName));
    }

    return Boolean(searchedRegion.full_name && !isOfferInsideSearchedRegion(offer.region.full_name, searchedRegion.full_name));
};

const isOfferInsideSearchedRegion = (offerRegionFullName: string, searchedRegionFullName: string): boolean => {
    const offerRegionPath = normalizeRegionPath(offerRegionFullName);
    const searchedRegionPath = normalizeRegionPath(searchedRegionFullName);

    return offerRegionPath === searchedRegionPath || offerRegionPath.startsWith(`${searchedRegionPath},`);
};

const splitRegionPath = (fullName: string): string[] => fullName.split(",").map((regionName) => regionName.trim());

const normalizeRegionPath = (fullName: string): string => splitRegionPath(fullName).map(normalizeRegionName).join(",");

const normalizeRegionName = (regionName: string): string => regionName.toLocaleLowerCase("pl-PL");
import {RegionType} from "@pg-rp/common-types";

interface IAddressOffer {
    address: string;
    region: {
        city?: string | null;
        district?: string | null;
        full_name: string;
        short_name: string;
        town?: string | null;
    };
    stats?: {distance_from_region?: {region?: string | null}} | null;
    street_name: string | null;
    street_number: string | null;
}

interface ISearchedRegion {
    full_name?: string;
    fromOfferStats?: boolean;
    type: number;
}

interface ISelectableSearchedRegion {
    full_name: string;
    id: number;
    type: number;
}

export interface IOfferCardAddressContext {
    /**
     * City names are needed only for legacy offer responses which expose a region path but no
     * explicit city/district fields (for example the vendor-detail offer list).
     */
    regionPathCityNames?: readonly string[];
    searchDistance?: number | string | null;
    searchedRegion?: ISearchedRegion;
}

interface IGetOfferCardAddressContextParams {
    searchedRegionQuery?: string | string[] | null;
    searchDistance?: number | string | null;
    selectedRegions: ISelectableSearchedRegion[];
}

export const getOfferCardAddressContext = ({
    searchedRegionQuery,
    searchDistance,
    selectedRegions
}: IGetOfferCardAddressContextParams): IOfferCardAddressContext => {
    const searchedRegionIds = Array.isArray(searchedRegionQuery) ? searchedRegionQuery : searchedRegionQuery ? [searchedRegionQuery] : [];
    const searchedRegion = searchedRegionIds.length === 1 ? selectedRegions.find((region) => region.id === Number(searchedRegionIds[0])) : undefined;

    return {searchedRegion, searchDistance};
};

/**
 * Builds the address shown on an offer tile. The locality is based on the search narrowing, while
 * radius searches additionally distinguish offers that belong to the searched region from offers
 * pulled in from its surroundings.
 */
export const getOfferCardAddress = (offer: IAddressOffer, context: IOfferCardAddressContext = {}): string => {
    const street = [offer.street_name, offer.street_number].filter(Boolean).join(" ");
    const locality = getOfferCardLocality(offer, context);

    return [street, locality].filter(Boolean).join(", ") || offer.address;
};

const getOfferCardLocality = (offer: IAddressOffer, context: IOfferCardAddressContext): string | null | undefined => {
    const {regionPathCityNames, searchedRegion, searchDistance} = context;
    const inferredLocality = getLocalityFromRegionPath(offer.region.full_name, regionPathCityNames);
    const city = offer.region.city || offer.region.town || inferredLocality.city;
    const hasExplicitDistrictData = Object.prototype.hasOwnProperty.call(offer.region, "district");
    const isCitySearch = searchedRegion?.type === RegionType.CITY || searchedRegion?.type === RegionType.TOWN;
    const regionLevelBelowSearch =
        !hasExplicitDistrictData && isCitySearch && searchedRegion.full_name
            ? getRegionLevelBelowSearch(offer.region.full_name, searchedRegion.full_name)
            : undefined;
    const hasRegionLevelBelowSearchData = regionLevelBelowSearch !== undefined;
    let district = inferredLocality.district;

    if (hasExplicitDistrictData) {
        district = offer.region.district;
    } else if (hasRegionLevelBelowSearchData) {
        district = regionLevelBelowSearch;
    }

    const isDistrictKnown = hasExplicitDistrictData || hasRegionLevelBelowSearchData || inferredLocality.hasMatchedCity;
    const isRadiusSearch = Number(searchDistance) > 0;
    const isOutsideSearchedRegion = isRadiusSearch && isOfferOutsideSearchedRegion(offer, searchedRegion, city);

    switch (searchedRegion?.type) {
        case RegionType.CITY:
        case RegionType.TOWN:
            return isOutsideSearchedRegion ? city || offer.region.short_name : isDistrictKnown ? district : offer.region.short_name;
        case RegionType.DISTRICT:
        case RegionType.NEIGHBORHOOD:
            return isOutsideSearchedRegion ? district || city || offer.region.short_name : null;
        default:
            return city || district || offer.region.short_name;
    }
};

interface IInferredLocality {
    city?: string;
    district?: string | null;
    hasMatchedCity: boolean;
}

const getLocalityFromRegionPath = (fullName: string, cityNames?: readonly string[]): IInferredLocality => {
    if (!cityNames?.length) {
        return {hasMatchedCity: false};
    }

    const regionPath = splitRegionPath(fullName);
    const normalizedCityNames = new Set(cityNames.map(normalizeRegionName));
    const cityIndex = regionPath.findIndex((regionName) => normalizedCityNames.has(normalizeRegionName(regionName)));

    if (cityIndex === -1) {
        return {hasMatchedCity: false};
    }

    return {
        city: regionPath[cityIndex],
        district: regionPath[cityIndex + 1] ?? null,
        hasMatchedCity: true
    };
};

/** `undefined` means paths do not match; `null` means the offer is exactly in the searched region. */
const getRegionLevelBelowSearch = (offerRegionFullName: string, searchedRegionFullName: string): string | null | undefined => {
    const offerRegionPath = splitRegionPath(offerRegionFullName);
    const searchedRegionPath = splitRegionPath(searchedRegionFullName);
    const isSearchedRegionPrefix = searchedRegionPath.every(
        (regionName, index) => normalizeRegionName(regionName) === normalizeRegionName(offerRegionPath[index] ?? "")
    );

    if (!isSearchedRegionPrefix) {
        return undefined;
    }

    return offerRegionPath[searchedRegionPath.length] ?? null;
};

/** Membership that cannot be determined counts as "inside", so the address stays as narrow as possible. */
const isOfferOutsideSearchedRegion = (offer: IAddressOffer, searchedRegion: ISearchedRegion | undefined, offerCity: string | null | undefined): boolean => {
    if (!searchedRegion) {
        return false;
    }

    if (searchedRegion.fromOfferStats) {
        const searchedRegionName = offer.stats?.distance_from_region?.region;

        return Boolean(searchedRegionName && offerCity && normalizeRegionName(offerCity) !== normalizeRegionName(searchedRegionName));
    }

    return Boolean(searchedRegion.full_name && !isOfferInsideSearchedRegion(offer.region.full_name, searchedRegion.full_name));
};

const isOfferInsideSearchedRegion = (offerRegionFullName: string, searchedRegionFullName: string): boolean => {
    const offerRegionPath = normalizeRegionPath(offerRegionFullName);
    const searchedRegionPath = normalizeRegionPath(searchedRegionFullName);

    return offerRegionPath === searchedRegionPath || offerRegionPath.startsWith(`${searchedRegionPath},`);
};

const splitRegionPath = (fullName: string): string[] => fullName.split(",").map((regionName) => regionName.trim());

const normalizeRegionPath = (fullName: string): string => splitRegionPath(fullName).map(normalizeRegionName).join(",");

const normalizeRegionName = (regionName: string): string => regionName.toLocaleLowerCase("pl-PL");
